
import { Component, Prop, Watch } from 'vue-property-decorator';
import VWidget from '@/components/VWidget.vue';
import ListBase from '@/lib/listbase';

import DateRangePicker from '../../components/date-range-picker.vue';
import EditableTable from './editable-table.vue';
import SummaryTable from './summary-table.vue';
import ExportVin from './export-vin.vue';

import { storeObjectives } from './services/transaction-service';

import {
    buildTableStructure,
    buildSummaryRows,
    buildObjectivesArray
} from './utils/table-builder';

import { getVinPopupData } from './utils/table-events';

import { exportInternalReportExcel } from './utils/excel-export';

declare module 'vue/types/vue' {
    interface Vue {
        $notify: {
            success: (message: string) => void;
            error: (message: string) => void;
        };
    }
}

interface SummaryJson {
    actual: { [centro: string]: any[] };
    anio_anterior: { [centro: string]: any[] };
}

interface VinPopupData {
    exportVinList: any[];
    exportTitleCenter: string;
    exportMonthName: string;
}

interface ExportInternalReportParams {
    rangeFrom: any;
    marcaSeleccionada: string;
    monthHeaders: string[];
    tableRows: any[];
    editableData: any;
    summaryRows: any[];
    previousYear: number;
}

@Component({
    components: {
        VWidget,
        DateRangePicker,
        EditableTable,
        SummaryTable,
        ExportVin,
    }
})
export default class SelectedDispache extends ListBase {
    menuFrom: boolean = false;
    showImport: boolean = false;
    isEdited: boolean = false;
    editModes: boolean[] = [];
    currentYear: number = new Date().getFullYear();

    editableData: any = {
        objetivo: [],
        marca: [],
        datos: []
    };

    tableRows = [
        { label: 'Concesionario', key: 'marca' },
        { label: 'Modelos', key: 'datos' },
        { label: 'Total', key: 'total' }
    ];

    summaryJson: SummaryJson = { actual: {}, anio_anterior: {} };

    @Prop({ default: '' }) readonly marcaSeleccionada!: string;
    @Prop({ default: '' }) readonly viewType!: string;

    @Prop({ required: true }) readonly rangeFrom!: any;
    @Prop({ default: false }) readonly loadingData!: boolean;

    @Prop({ default: null }) readonly summaryData!: any;

    @Prop({ required: true }) readonly hasAdmin!: boolean;

    showExportVin = false;
    exportVinList: any[] = [];
    exportTitleCenter: string = '';
    exportMonthName: string = '';

    localMarcaSeleccionada: string = '';

    listDealers: string[] = [];

    localConfirmSave = false;

    @Watch('localConfirmSave')
    onConfirmSaveChanged(newVal: boolean) {
        if (!newVal) {
            this.$emit('cancel');
        }
    }

    @Watch('summaryData', { immediate: true, deep: true })
    onSummaryDataChanged(newVal: any) {
        if (newVal) {
            this.summaryJson = newVal;
            this.listDealers = newVal.dealers || [];
            this.buildTableStructure();
        }
    }

    showConfirmation() {
        this.localConfirmSave = true;
    }

    summaryRows: any[] = [];

    changesTracker: Set<string> = new Set();
    centersMap: { [key: string]: string } = {};

    get brandId(): string {
        return this.marcaSeleccionada.split('|')[0] || '';
    }

    get brandName(): string {
        return this.marcaSeleccionada.split('|')[1] || '';
    }


    get computedTitle(): string {
        let baseTitle = '';
        switch (this.viewType) {
            case 'internal':
                baseTitle = 'Reporte Despachos AASA - Internos';
                break;
            case 'external':
                baseTitle = 'Reporte Despachos AASA - Externos';
                break;
            case 'total':
                baseTitle = 'Reporte Despachos AASA - Resumen';
                break;
            default:
                baseTitle = 'Reporte Despachos AASA';
                break;
        }
        return baseTitle
    }

    get isMarcaSelected(): boolean {
        return !!(this.marcaSeleccionada && this.marcaSeleccionada.trim());
    }

    get isDealer() {
        return false;
    }

    get previousYear() {
        return this.rangeFrom.year - 1;
    }

    get monthHeaders() {
        const months = this.gCommonList('months').map(m => m.name);
        return months.slice(this.rangeFrom.from, this.rangeFrom.to + 1);
    }

    created() {
        this.initializeEditableData();
    }

    initializeEditableData() {
        const totalMainColumns = this.monthHeaders.length + 2;
        this.tableRows.forEach(row => {
            this.editableData[row.key] = Array.from(
                { length: totalMainColumns },
                () => ({ real: 0, objetivo: 0 })
            );
        });
        this.editModes = Array(totalMainColumns).fill(false);
    }

    onEdit() {
        this.isEdited = true;
        this.$emit('edit');
    }

    toggleEditMode(columnIndex: number) {
        this.$set(this.editModes, columnIndex, !this.editModes[columnIndex]);
    }

    async saveData() {
        this.localConfirmSave = false;
        try {
            const objectives = this.buildObjectivesArray();

            const response = await storeObjectives(this.$store, objectives);

            if (response.success) {
                this.$toast.success(response.message || 'Objetivos guardados correctamente');
                this.isEdited = false;
                this.editModes = this.editModes.map(() => false);
                this.changesTracker.clear();
                this.$emit('data-saved');
            } else {
                this.$toast.error(response.error || 'Error al guardar objetivos');
            }
        } catch (error) {
            const err = error as any;
            if (err.response && err.response.data) {
                console.error('Error en el backend:', err.response.data);
                this.$toast.error(err.response.data.error || 'Error en el servidor');
            } else {
                console.error('Error desconocido:', err);
                this.$toast.error('Error desconocido al guardar datos');
            }
        }
    }

    updateTableWithImportedData(importedSheet: { headers: string[]; items?: any[]; rows?: any[] }): void {
        const dataRows = importedSheet.items || importedSheet.rows;
        if (!dataRows) {
            console.error("No se encontraron filas en los datos importados.");
            return;
        }

        this.changesTracker.clear();
        this.isEdited = false;

        let currentConcesionario: string | null = null;
        const totalMainColumns = this.monthHeaders.length + 2;

        for (const row of dataRows) {
            if (row["CONCESIONARIO"] && row["CONCESIONARIO"].trim() !== "") {
                currentConcesionario = row["CONCESIONARIO"].trim().toLowerCase();

                const dealerKey = `center-${currentConcesionario}`;
                const existingDealerIndex = this.tableRows.findIndex(r => r.key === dealerKey);
                if (existingDealerIndex === -1) {
                    const displayDealer = this.toCanonicalName(row["CONCESIONARIO"]);
                    const newDealerRow = { label: displayDealer, key: dealerKey };
                    this.tableRows.push(newDealerRow);
                    this.editableData[dealerKey] = this.createEmptyRow(totalMainColumns);
                }
                continue;
            }

            if (!currentConcesionario) {
                console.warn("No se definió un concesionario. Se omite la fila:", row);
                continue;
            }

            const modeloRaw = row["MODELOS"] ? row["MODELOS"].trim() : "";
            if (!modeloRaw || modeloRaw === "-") continue;

            const modelo = modeloRaw.toLowerCase();
            const expectedKey = `item-${currentConcesionario}-${modelo}`;
            let targetRow = this.tableRows.find(r => r.key === expectedKey);

            if (!targetRow) {
                const newLabel = modeloRaw;
                const newRow = { label: newLabel, key: expectedKey };

                const dealerKey = `center-${currentConcesionario}`;
                const dealerIndex = this.tableRows.findIndex(r => r.key === dealerKey);

                let insertIndex = dealerIndex + 1;
                while (
                    insertIndex < this.tableRows.length &&
                    this.tableRows[insertIndex].key.startsWith(`item-${currentConcesionario}-`)
                ) {
                    insertIndex++;
                }

                this.tableRows.splice(insertIndex, 0, newRow);
                this.editableData[newRow.key] = this.editableData[newRow.key] || this.createEmptyRow(totalMainColumns);
                targetRow = newRow;
            }

            for (let i = 0; i < this.monthHeaders.length; i++) {
                const monthKey = this.monthHeaders[i].substring(0, 3).toUpperCase();
                if (row[monthKey] !== undefined) {
                    const currentObjetivo = this.parseNumber(row[monthKey]);
                    this.editableData[targetRow.key][i].objetivo = currentObjetivo;

                    if (typeof this.editableData[targetRow.key][i].real === 'undefined') {
                        this.editableData[targetRow.key][i].real = 0;
                    }
                    const month = this.rangeFrom.from + i + 1;
                    const changeKey = `${currentConcesionario}-${modelo}-${this.rangeFrom.year}-${month}`;
                    this.changesTracker.add(changeKey);
                }
            }
        }

        this.isEdited = true;
        this.$emit('edit');
    }

    private parseNumber(value: any): number {
        const str = String(value ?? '').trim();
        if (['', '-'].includes(str)) return 0;
        return Math.max(0, parseFloat(str.replace(',', '.'))) || 0;
    }

    private toCanonicalName(name: string): string {
        return name
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    }

    private createEmptyRow(length: number): any[] {
        return Array.from({ length }, () => ({ real: 0, objetivo: 0 }));
    }

    private buildObjectivesArray() {
        return buildObjectivesArray(
            this.changesTracker,
            this.editableData,
            this.centersMap,
            this.rangeFrom,
            this.getManufacturerId
        );
    }

    get getManufacturerId() {
        return this.marcaSeleccionada.split('|')[0];
    }

    onTrackChange(payload: { rowKey: string; colIndex: number; newValue: number }) {
        const { rowKey, colIndex } = payload;

        if (!rowKey.startsWith('item-')) return;

        const splitted = rowKey.split('-');

        if (splitted.length < 3) return;

        const dealerKey = splitted[1];
        const modelKey = splitted[2];
        const month = this.rangeFrom.from + colIndex + 1;

        const changeKey = `${dealerKey}-${modelKey}-${this.rangeFrom.year}-${month}`;

        this.changesTracker.add(changeKey);

        this.isEdited = true;
    }

    validateYear(year: number) {
        if (year <= this.currentYear) {
            this.rangeFrom.year = year;
        } else {
            this.rangeFrom.year = this.currentYear;
        }
    }

    private buildTableStructure(): void {
        if (!this.summaryData) return;

        const { tableRows, editableData, centersMap } = buildTableStructure(
            this.summaryJson,
            this.monthHeaders,
            this.rangeFrom,
            this.gCommonList.bind(this),
            this.viewType
        );

        this.centersMap = centersMap;
        this.tableRows = tableRows;
        this.editableData = editableData;

        this.buildSummaryRows();
    }

    private buildSummaryRows(): void {
        this.summaryRows = buildSummaryRows(
            this.summaryJson,
            this.monthHeaders,
            this.rangeFrom,
            this.gCommonList.bind(this),
            this.viewType
        );
    }

    openVinPopup(payload: { row: any; colIndex: number }) {
        const data: VinPopupData = getVinPopupData(
            this.summaryJson,
            this.rangeFrom,
            this.monthHeaders,
            this.gCommonList.bind(this),
            payload.row.key,
            payload.colIndex,
            payload.row.vins
        );

        // eslint-disable-next-line no-unused-vars
        this.exportVinList = data.exportVinList.map(({ dealer, ...rest }) => rest);

        this.exportTitleCenter = data.exportTitleCenter;
        this.exportMonthName = data.exportMonthName;
        this.showExportVin = true;
    }

    async exportData() {
        const params: ExportInternalReportParams = {
            rangeFrom: this.rangeFrom,
            marcaSeleccionada: this.brandName,
            monthHeaders: this.monthHeaders,
            tableRows: this.tableRows,
            editableData: this.editableData,
            summaryRows: this.summaryRows,
            previousYear: this.previousYear
        };

        try {
            await exportInternalReportExcel.call(this, params);
            this.$toast.success("Reporte generado con éxito");
        } catch (error) {
            console.warn("Error en exportInternalReportExcel (pero el reporte se generó):", error);
            this.$toast.success("Reporte generado con éxito");
        }
    }

    @Watch('summaryRows', { deep: true })
    onSummaryRowsChanged() {
    }

    private handleSummaryClick(payload: { row: any; cellIndex: number }): void {
        const isTotal = payload.cellIndex >= this.monthHeaders.length;

        const data: VinPopupData = this.getSummaryVinPopupData(payload.row, payload.cellIndex, isTotal);

        this.exportVinList = data.exportVinList;
        this.exportTitleCenter = data.exportTitleCenter;
        this.exportMonthName = data.exportMonthName;
        this.showExportVin = true;
    }

    private getSummaryVinPopupData(row: any, cellIndex: number, isTotal: boolean): VinPopupData {
        const vins = row.vins || [];
        let exportVinList: any[] = [];

        if (isTotal) {
            exportVinList = vins.filter((vin: any) =>
                vin.Anyo === this.rangeFrom.year
            );
        } else {
            const targetMonth = this.rangeFrom.from + cellIndex + 1;
            exportVinList = vins.filter((vin: any) =>
                vin.Mes === targetMonth &&
                vin.Anyo === this.rangeFrom.year
            );
        }

        return {
            exportVinList: exportVinList,
            exportTitleCenter: this.brandName,
            exportMonthName: isTotal
                ? 'Total'
                : this.gCommonList('months')[this.rangeFrom.from + cellIndex].name
        };
    }

    cancelImportedData() {
        this.$toast.info('Importación cancelada. Se han eliminado los datos importados.');
        this.isEdited = false;
        this.buildTableStructure();
    }
}
